// Community types based on Doc 4: Communities & Social

import type { BaseEntity, UUID } from './common.types'
import type { TrackRecord, Challenge, UserChallenge } from './challenge.types'

/**
 * Classification of a community, determining default trust level and feature set.
 *
 * @remarks
 * The 6 core types from Doc 5 (Communities & Social). School communities use
 * `institutional` with {@link SchoolCommunityLevel} and `schoolId` fields.
 * Visibility (`public`/`private`/`invite_only`) is a separate field on {@link Community}.
 *
 * - `family` -- Family groups with high trust
 * - `trusted_friends` -- Close friends with elevated sharing defaults
 * - `casual_friends` -- Casual friend groups
 * - `interest_hobby` -- Interest/hobby-based open communities
 * - `other` -- Miscellaneous / general-purpose communities
 * - `institutional` -- School and organizational communities
 */
export type CommunityType =
  | 'family'
  | 'trusted_friends'
  | 'casual_friends'
  | 'interest_hobby'
  | 'other'
  | 'institutional'

/**
 * Default settings for each community type
 */
export const COMMUNITY_TYPE_DEFAULTS: Record<CommunityType, {
  visibility: CommunityVisibility
  joinModel: string
  giftingDefault: boolean
}> = {
  family:          { visibility: 'private', joinModel: 'invite_only', giftingDefault: true },
  trusted_friends: { visibility: 'private', joinModel: 'invite_only', giftingDefault: true },
  casual_friends:  { visibility: 'private', joinModel: 'invite_only', giftingDefault: false },
  interest_hobby:  { visibility: 'public',  joinModel: 'requestable', giftingDefault: false },
  other:           { visibility: 'public',  joinModel: 'auto_join',   giftingDefault: false },
  institutional:   { visibility: 'private', joinModel: 'auto_join',   giftingDefault: false },
}

/**
 * Granularity level for school-type communities.
 * Only applicable when `CommunityType` is `'institutional'` with a `schoolId`.
 */
export type SchoolCommunityLevel = 'full_school' | 'grade' | 'class'

/**
 * Controls how users can discover and join a community.
 *
 * @remarks
 * - `public` -- Visible in search; anyone can join.
 * - `private` -- Visible in search; requires approval to join.
 * - `invite_only` -- Hidden from search; membership by invitation only.
 */
export type CommunityVisibility = 'public' | 'private' | 'invite_only'

/**
 * Controls the mechanism for joining a community, independent of visibility.
 *
 * @remarks
 * Decoupled from `CommunityVisibility` so a community can be publicly visible
 * but still require approval to join, or be invite-only but listed in search.
 * - `open` -- Anyone can join immediately.
 * - `approval` -- Join request requires moderator/admin approval.
 * - `invite_only` -- Only invited users can join.
 */
export type JoinModel = 'open' | 'approval' | 'invite_only'

/**
 * Role hierarchy within a community: `owner` > `admin` > `moderator` > `member`.
 */
export type CommunityRole = 'owner' | 'admin' | 'moderator' | 'member'

/**
 * Current state of a user's membership in a community.
 * `pending` indicates a join request awaiting approval; `invited` indicates an unseen invitation.
 */
export type MembershipStatus = 'active' | 'pending' | 'invited' | 'banned'

/**
 * Discriminator for community feed items, organized into 3 feed categories.
 *
 * @remarks
 * - **Bucket List feed**: `bucket_list`, `challenge_share`
 * - **Track Record feed**: `track_record`, `milestone`
 * - **Discussion feed**: `discussion`, `announcement`
 *
 * Each community displays these three feeds as separate tabs.
 */
export type FeedItemType =
  | 'bucket_list'      // Challenge someone wants to do (shared to community)
  | 'track_record'     // Shared track record (completed challenge)
  | 'announcement'     // Admin announcement
  | 'milestone'        // Milestone achievement
  | 'challenge_share'  // Challenge recommendation
  | 'discussion'       // Discussion post

/**
 * A community group where users share challenges, track records, and discussions.
 *
 * @remarks
 * DoCurious supports 6+ community types (see {@link CommunityType}). School
 * communities have additional fields for grade/class scoping and auto-membership.
 * Each community has three feed tabs: Bucket List, Track Record, and Discussion.
 *
 * @example
 * ```ts
 * const community: Community = {
 *   id: 'com-010',
 *   createdAt: '2025-09-01T00:00:00Z',
 *   updatedAt: '2025-09-01T00:00:00Z',
 *   name: 'Mrs. Lee — 5th Grade Science',
 *   slug: 'mrs-lee-5th-science',
 *   type: 'institutional',
 *   schoolId: 'sch-001',
 *   schoolCommunityLevel: 'class',
 *   className: '5A',
 *   ownerUserId: 'usr-teacher-01',
 *   visibility: 'private',
 *   allowMemberPosts: true,
 *   requirePostApproval: false,
 *   isAutoMembership: true,
 *   memberCount: 28,
 *   postCount: 45,
 * }
 * ```
 */
export interface Community extends BaseEntity {
  name: string
  description?: string
  /** URL-safe identifier used in routing */
  slug: string

  type: CommunityType

  // School-specific fields (only for type === 'institutional' with schoolId)
  schoolId?: UUID
  schoolCommunityLevel?: SchoolCommunityLevel
  /** Display label for grade-level communities (e.g., "5th Grade", "Grade 10") */
  gradeLevel?: string
  /** Reference to a specific class for class-level communities */
  classId?: UUID
  /** Display label for the class (e.g., "5A", "Math Period 3") */
  className?: string

  ownerUserId: UUID

  visibility: CommunityVisibility
  /** How users can join this community (independent of visibility) */
  joinModel?: JoinModel
  /** Whether regular members can create posts (false = admin-only posting) */
  allowMemberPosts: boolean
  /** Whether new posts require moderator approval before appearing in the feed */
  requirePostApproval: boolean

  /** When true, new students/teachers are automatically added (school communities) */
  isAutoMembership: boolean

  avatarUrl?: string
  bannerUrl?: string

  /** Whether comments on track record entries are enabled (default: false) */
  trackRecordCommentsEnabled?: boolean

  memberCount: number
  postCount: number

  /** The current authenticated user's membership record, if they belong to this community */
  currentUserMembership?: CommunityMembership
}

/**
 * A user's membership record within a {@link Community}, including role and permissions.
 */
export interface CommunityMembership extends BaseEntity {
  communityId: UUID
  userId: UUID
  role: CommunityRole
  status: MembershipStatus

  /** Whether this member can create posts in the community feed */
  canPost: boolean
  /** Whether this member can moderate (approve/remove) posts and comments */
  canModerate: boolean
  /** Whether this member can send invitations to new users */
  canInvite: boolean

  joinedAt?: string
  invitedAt?: string
  /** User ID of the person who invited this member */
  invitedByUserId?: UUID

  // Populated
  user?: {
    id: UUID
    displayName: string
    avatarUrl?: string
  }
}

/**
 * A single item in a community feed (Bucket List, Track Record, or Discussion).
 *
 * @remarks
 * The `type` field determines which feed tab this item appears in and what
 * referenced entities are populated (`trackRecordId` for TR feed, `challengeId`
 * for Bucket List feed, etc.).
 */
export interface FeedItem extends BaseEntity {
  communityId: UUID
  authorUserId: UUID
  type: FeedItemType

  /** User-authored text content (optional for TR shares which use the TR itself) */
  textContent?: string

  /** Referenced Track Record (for `track_record` and `milestone` types) */
  trackRecordId?: UUID
  /** Referenced Challenge (for `bucket_list` and `challenge_share` types) */
  challengeId?: UUID

  /** Whether this item is pinned to the top of its feed */
  isPinned: boolean
  /** Whether this is an admin announcement (always pinned, special styling) */
  isAnnouncement: boolean

  likeCount: number
  commentCount: number

  // Populated
  author?: {
    id: UUID
    displayName: string
    avatarUrl?: string
  }
  trackRecord?: TrackRecord
}

/**
 * A threaded comment on a {@link FeedItem}.
 */
export interface FeedComment extends BaseEntity {
  feedItemId: UUID
  authorUserId: UUID
  /** Parent comment ID for nested replies; `undefined` for top-level comments */
  parentCommentId?: UUID

  content: string

  // Populated
  author?: {
    id: UUID
    displayName: string
    avatarUrl?: string
  }
}

/**
 * A "like" reaction on a {@link FeedItem} (one per user per item).
 */
export interface FeedLike {
  id: UUID
  feedItemId: UUID
  userId: UUID
  createdAt: string
}

/**
 * An invitation to join a community, sent by email or to an existing user.
 *
 * @remarks
 * Invitations can target either a registered user (`invitedUserId`) or an email
 * address (`invitedEmail`) for users who have not yet signed up.
 */
export interface CommunityInvitation extends BaseEntity {
  communityId: UUID
  /** Email address for inviting non-registered users */
  invitedEmail?: string
  /** User ID for inviting existing platform members */
  invitedUserId?: UUID
  invitedByUserId: UUID

  status: 'pending' | 'accepted' | 'declined' | 'expired'
  /** ISO datetime after which this invitation is no longer valid */
  expiresAt: string

  // Populated
  community?: Community
}

/**
 * A content flag (report) submitted by a user against community content.
 *
 * @remarks
 * Used for community-level moderation. Admins/moderators review flagged content
 * and take action (warn, remove, ban).
 */
export interface ContentFlag extends BaseEntity {
  communityId: UUID
  reporterUserId: UUID
  /** Type of content being flagged */
  contentType: 'feed_item' | 'comment' | 'user'
  /** ID of the flagged content */
  contentId: UUID
  /** Reason for the flag */
  reason: 'spam' | 'harassment' | 'inappropriate' | 'misinformation' | 'other'
  /** Optional description from the reporter */
  description?: string
  /** Current review status */
  status: 'pending' | 'reviewed' | 'dismissed'
  /** Action taken by the moderator after review */
  actionTaken?: 'none' | 'warning' | 'content_removed' | 'user_banned'
  /** Moderator's notes about the review decision */
  reviewNotes?: string
  reviewedByUserId?: UUID
  reviewedAt?: string
}

/**
 * Filter parameters for the community search/browse API.
 */
export interface CommunityFilters {
  search?: string
  visibility?: CommunityVisibility
  isMember?: boolean
  // Type filters
  type?: CommunityType
  schoolId?: UUID
  schoolCommunityLevel?: SchoolCommunityLevel
}

/**
 * Lifecycle state of a {@link CommunityAssignment}.
 */
export type AssignmentStatus = 'active' | 'completed' | 'archived'

/**
 * Individual student's progress state on an assigned challenge.
 */
export type StudentAssignmentStatus = 'not_started' | 'in_progress' | 'submitted' | 'completed'

/**
 * A challenge assigned by a teacher to all members of a class community.
 *
 * @remarks
 * Teachers create assignments from their class community dashboard. Each
 * assignment tracks aggregate progress stats across all students. Individual
 * student progress is tracked via {@link CommunityStudentProgress}.
 */
export interface CommunityAssignment extends BaseEntity {
  communityId: UUID
  challengeId: UUID
  assignedByUserId: UUID

  /** Optional ISO date by which students should complete the challenge */
  dueDate?: string

  status: AssignmentStatus

  /** Teacher's instructions or context for the assignment */
  assignmentNote?: string

  // Aggregate stats
  totalStudents: number
  completedCount: number
  inProgressCount: number
  submittedCount: number

  // Populated
  challenge?: Challenge
  assignedBy?: {
    id: UUID
    displayName: string
    avatarUrl?: string
  }
}

/**
 * An individual student's progress on a {@link CommunityAssignment}.
 *
 * @remarks
 * One record per student per assignment. The `progressPercent` is computed from
 * milestone completion for structured challenges, or from status transitions
 * for simple challenges.
 */
export interface CommunityStudentProgress extends BaseEntity {
  assignmentId: UUID
  userId: UUID
  /** Links to the student's {@link UserChallenge} once they start */
  userChallengeId?: UUID

  status: StudentAssignmentStatus
  /** Completion percentage (0-100) */
  progressPercent: number

  startedAt?: string
  submittedAt?: string
  completedAt?: string

  // Populated
  user?: {
    id: UUID
    displayName: string
    firstName?: string
    lastName?: string
    avatarUrl?: string
  }
  userChallenge?: UserChallenge
  trackRecordId?: UUID
}
